import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Business } from 'src/app/models/register-business';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from 'src/app/shared/error-handler/error-handler.service';
import {
  downscaleImageFile,
  formatBytes,
  UPLOAD_MAX_BYTES,
  UPLOAD_MAX_DECODE_BYTES
} from 'src/app/common/image-downscale';
import { MiniMenuPromoModalComponent } from '../mini-menu-promo-modal/mini-menu-promo-modal.component';
import {
  MiniMenuLandingCropModalComponent,
  MiniMenuLandingCropResult
} from '../mini-menu-landing-crop-modal/mini-menu-landing-crop-modal.component';
import {
  calendarDaysSince,
  DEFAULT_MINI_MENU_SETTINGS,
  emptyPromoCrops,
  MINI_MENU_FONTS,
  MINI_MENU_PROMO_RULES,
  MiniMenuEntitlement,
  MiniMenuFoodieNewsText,
  MiniMenuMediaAsset,
  MiniMenuMenuText,
  MiniMenuPromo,
  MiniMenuPromoCrops,
  MiniMenuPromoStatus,
  MiniMenuService,
  MiniMenuSiteAdmin,
  MiniMenuSettings,
  MiniMenuTranslationBundle,
  miniMenuFontStack,
  workingDaysSince
} from 'src/app/services/mini-menu.service';
import {
  MiniMenuAppearanceModalComponent,
  MiniMenuAppearanceResult
} from '../mini-menu-appearance-modal/mini-menu-appearance-modal.component';
import { MiniMenuQrPosterModalComponent } from '../mini-menu-qr-poster-modal/mini-menu-qr-poster-modal.component';

interface KeyTitleOption {
  id: string;
  title: string;
}

@Component({
  selector: 'app-mini-menu-settings',
  templateUrl: './mini-menu-settings.component.html',
  styleUrls: ['./mini-menu-settings.component.scss']
})
export class MiniMenuSettingsComponent implements OnChanges, OnDestroy {
  @Input() businessDetails: Business;

  @ViewChild('promoSubmitModal') promoSubmitModal!: TemplateRef<any>;
  @ViewChild('promoResubmitModal') promoResubmitModal!: TemplateRef<any>;
  @ViewChild('promoSuspendModal') promoSuspendModal!: TemplateRef<any>;
  @ViewChild('promoDeleteModal') promoDeleteModal!: TemplateRef<any>;
  @ViewChild('promoSuspensionExpiredModal') promoSuspensionExpiredModal!: TemplateRef<any>;

  settings: MiniMenuSettings = JSON.parse(JSON.stringify(DEFAULT_MINI_MENU_SETTINGS));
  entitlement: MiniMenuEntitlement = { available: false, plan: 'Not available' };
  site: MiniMenuSiteAdmin | null = null;
  isLoading = false;
  isSaving = false;
  loadFailed = false;
  translation: MiniMenuTranslationBundle = this.emptyTranslation();

  fonts: KeyTitleOption[] = MINI_MENU_FONTS;
  fontSelect: KeyTitleOption = MINI_MENU_FONTS[0];
  languages: KeyTitleOption[] = [
    { id: 'en', title: 'English' },
    { id: 'zh-CN', title: 'Chinese' }
  ];
  languageSelect: KeyTitleOption = this.languages[0];

  readonly promoRules = MINI_MENU_PROMO_RULES;
  /** The three device crops on the single landing background image. */
  landingCrops: MiniMenuPromoCrops = emptyPromoCrops();
  /** Promotion the pill menu is currently acting on, so only that row shows a busy state. */
  promoBusyId: number | null = null;
  /** Promotion awaiting a decision in one of the confirm modals. */
  promoInFlight: MiniMenuPromo | null = null;

  private adminPreviewSubscriptions = new Subscription();
  private readonly adminPreviewUrls = new Map<string, string>();

  readonly seededDemoUrl = 'https://menu.hungery.id/staging';

  formGroup = new FormGroup({
    LocationTag: new FormControl('', Validators.maxLength(24)),
    Eyebrow: new FormControl(''),
    Subline: new FormControl('')
  });

  constructor(
    private miniMenuService: MiniMenuService,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {}

  get publicMenuUrl(): string {
    const publicPath = this.site?.publicPath
      || (this.site?.slug ? `/${encodeURIComponent(this.site.slug)}` : '');
    if (!publicPath) {
      return '';
    }

    const publicRoot = String(environment.miniMenuPublicRoot || 'https://menu.hungery.id')
      .replace(/\/+$/, '');
    return `${publicRoot}/${publicPath.replace(/^\/+/, '')}`;
  }

  get publishedBranchTitle(): string {
    const branchId = this.site?.publishedBranchId;
    return this.site?.availableBranches?.find((branch) => branch.id === branchId)?.title || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessDetails'] && this.businessDetails?.businessPartnerId) {
      this.loadSite(this.businessDetails.businessPartnerId);
    }
  }

  ngOnDestroy(): void {
    this.clearAdminMediaPreviews();
  }

  save(): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId || !this.site?.isAvailable || this.isSaving) {
      return;
    }

    this.collectIntoSettings();
    this.isSaving = true;
    const enabledLanguages = [
      { code: 'en', title: 'English', shortTitle: 'EN', sortOrder: 0 },
      ...(this.settings.chineseEnabled
        ? [{ code: 'zh-CN', title: 'Chinese', shortTitle: 'ZH.CN', sortOrder: 10 }]
        : [])
    ];
    const defaultLanguage = enabledLanguages.some((language) => language.code === this.settings.defaultLanguage)
      ? this.settings.defaultLanguage
      : 'en';
    this.miniMenuService.updatePresentation(businessPartnerId, {
      locationTag: this.settings.locationTag,
      eyebrow: this.settings.eyebrow,
      subline: this.settings.subline,
      font: this.settings.theme.font,
      backgroundColor: this.settings.theme.backgroundColor,
      uiColor: this.settings.theme.uiColor,
      contentColor: this.settings.theme.contentColor,
      accentColor: this.settings.theme.accentColor,
      rowVersion: this.site.presentation?.rowVersion || null
    }).pipe(
      switchMap(() => this.miniMenuService.updateLanguages(
        businessPartnerId,
        defaultLanguage,
        enabledLanguages
      )),
      switchMap(() => this.miniMenuService.updateFoodieNewsOrder(businessPartnerId, this.settings.promos)),
      switchMap((site) => this.settings.chineseEnabled
        ? this.miniMenuService.updateTranslations(businessPartnerId, this.translation)
        : of(site))
    ).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isSaving = false;
        this.toastr.success('MiniMenu details saved.');
        // Saving assigns ids to brand-new promotions, so this is the first moment they can
        // be submitted. Same shape as Save Details then "Publish business?" on the form.
        this.promptSubmitDrafts();
      },
      error: (error) => {
        this.isSaving = false;
        this.toastr.error(ErrorHandlerService.bodyMessage(error) || 'MiniMenu details could not be saved.');
      }
    });
  }

  togglePublished(event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextPublished = input.checked;
    const businessPartnerId = this.businessDetails?.businessPartnerId;

    if (!this.site?.isAvailable || !businessPartnerId || !this.site.publishedBranchId || !this.site.slug) {
      input.checked = this.site?.isPublished === true;
      return;
    }

    this.isSaving = true;
    this.miniMenuService.updateSite(businessPartnerId, {
      publishedBranchId: this.site.publishedBranchId,
      slug: this.site.slug,
      isPublished: nextPublished,
      defaultLanguage: this.site.defaultLanguage || 'en'
    }).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isSaving = false;
        this.toastr.success(site.isPublished ? 'MiniMenu is published.' : 'MiniMenu is hidden.');
      },
      error: (error) => {
        input.checked = this.site?.isPublished === true;
        this.isSaving = false;
        this.toastr.error(error?.error?.message || 'MiniMenu publishing could not be updated.');
      }
    });
  }

  private loadSite(businessPartnerId: number): void {
    this.isLoading = true;
    this.loadFailed = false;
    this.miniMenuService.getSite(businessPartnerId).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isLoading = false;
      },
      error: () => {
        this.site = null;
        this.entitlement = { available: false, plan: 'Unavailable' };
        this.loadFailed = true;
        this.isLoading = false;
      }
    });
  }

  private applySite(site: MiniMenuSiteAdmin): void {
    this.clearAdminMediaPreviews();
    this.site = site;
    this.entitlement = {
      available: site.isAvailable,
      plan: site.isAvailable ? 'Available' : 'Not available'
    };
    this.settings = this.miniMenuService.settingsFromSite(site);
    const landing = site.presentation?.landingBackground;
    this.landingCrops = {
      mobile: landing?.mobileCrop || null,
      tablet: landing?.tabletCrop || null,
      tabletPortrait: landing?.tabletPortraitCrop || null
    };
    this.translation = site.translations?.find((item) => item.languageCode === 'zh-CN')
      || this.emptyTranslation();
    this.patchFromSettings();
    this.loadAdminMediaPreviews(site);
    this.promptExpiredSuspensions();
  }

  /**
   * Draft media is private, so its public URL correctly returns 404. Fetch every settings
   * preview through HttpClient instead: the auth interceptor supplies the bearer token even
   * when staging and its API are on different domains.
   */
  private loadAdminMediaPreviews(site: MiniMenuSiteAdmin): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId) {
      return;
    }

    this.loadAdminMediaPreview(
      'presentation-background',
      businessPartnerId,
      site.presentation?.landingBackground,
      (url) => { this.settings.teaserBackgroundUrl = url; }
    );
    this.loadAdminMediaPreview(
      'presentation-landing-logo',
      businessPartnerId,
      site.presentation?.landingLogo,
      (url) => { this.settings.theme.landingLogoUrl = url; }
    );
    this.loadAdminMediaPreview(
      'presentation-banner-logo',
      businessPartnerId,
      site.presentation?.bannerLogo,
      (url) => { this.settings.theme.bannerLogoUrl = url; }
    );
    this.settings.promos.forEach((promo) => this.loadAdminMediaPreview(
      `promotion-${promo.promotionId}`,
      businessPartnerId,
      promo.mediaAsset,
      (url) => { promo.image = url; }
    ));
  }

  private loadAdminMediaPreview(
    key: string,
    businessPartnerId: number,
    asset: MiniMenuMediaAsset | null | undefined,
    applyUrl: (url: string) => void
  ): void {
    if (!asset) {
      return;
    }
    this.revokeAdminMediaPreview(key);
    const subscription = this.miniMenuService.getAdminMediaPreview(businessPartnerId, asset).subscribe({
      next: (blob) => {
        if (!blob?.size) {
          return;
        }
        const url = URL.createObjectURL(blob);
        this.adminPreviewUrls.set(key, url);
        applyUrl(url);
      },
      // A missing private preview should leave the placeholder visible without turning a
      // successful settings load into an error for the entire form.
      error: () => { /* no preview available */ }
    });
    this.adminPreviewSubscriptions.add(subscription);
  }

  private revokeAdminMediaPreview(key: string): void {
    const url = this.adminPreviewUrls.get(key);
    if (url) {
      URL.revokeObjectURL(url);
      this.adminPreviewUrls.delete(key);
    }
  }

  private clearAdminMediaPreviews(): void {
    this.adminPreviewSubscriptions.unsubscribe();
    this.adminPreviewSubscriptions = new Subscription();
    this.adminPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    this.adminPreviewUrls.clear();
  }

  addPromotion(): void {
    this.openPromoModal(this.miniMenuService.createPromo());
  }

  openPromoModal(existing?: MiniMenuPromo, index?: number): void {
    const promo = existing || this.miniMenuService.createPromo();
    const targetIndex = typeof index === 'number'
      ? index
      : this.settings.promos.findIndex((item) => item.id === promo.id);

    const modalRef = this.modalService.open(MiniMenuPromoModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-promo-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.isNew = targetIndex < 0;
    modalRef.componentInstance.promo = JSON.parse(JSON.stringify(promo));

    modalRef.result.then(
      (saved: MiniMenuPromo) => {
        const businessPartnerId = this.businessDetails?.businessPartnerId;
        if (!saved || !businessPartnerId) {
          return;
        }
        this.isSaving = true;
        this.miniMenuService.saveFoodieNews(businessPartnerId, saved, Math.max(0, targetIndex)).pipe(
          switchMap((record) => {
            saved.promotionId = record.foodieNewsId;
            saved.rowVersion = record.rowVersion;
            if (saved.pendingImageFile) {
              return this.miniMenuService.uploadMedia(
                businessPartnerId,
                4,
                saved.pendingImageFile,
                record.foodieNewsId
              ).pipe(
                switchMap((asset) => this.miniMenuService.updateMediaCrop(
                  businessPartnerId,
                  asset.id,
                  saved.crops,
                  saved.title
                ))
              );
            }
            if (!saved.image && saved.mediaId) {
              return this.miniMenuService.deleteMedia(businessPartnerId, saved.mediaId);
            }
            return of(null);
          }),
          switchMap(() => this.miniMenuService.getSite(businessPartnerId))
        ).subscribe({
          next: (site) => {
            this.applySite(site);
            this.isSaving = false;
            this.toastr.success('What’s On draft saved.');
          },
          error: (error) => this.handlePromotionSaveError(businessPartnerId, saved, error)
        });
      },
      () => { /* dismissed — no change */ }
    );
  }

  /**
   * A new draft may have been created before its later image or crop request failed. Reload
   * it so a retry edits the saved draft instead of silently creating a duplicate record.
   */
  private handlePromotionSaveError(
    businessPartnerId: number,
    saved: MiniMenuPromo,
    error: any
  ): void {
    const message = ErrorHandlerService.bodyMessage(error) || 'What’s On could not be saved.';
    if (!saved.promotionId) {
      this.isSaving = false;
      this.toastr.error(message);
      return;
    }

    this.miniMenuService.getSite(businessPartnerId).subscribe({
      next: (site) => {
        this.applySite(site);
        this.isSaving = false;
        this.toastr.warning(`The draft was saved, but its image could not be completed. ${message}`);
      },
      error: () => {
        this.isSaving = false;
        this.toastr.warning(
          'The draft was saved, but its image could not be completed. Refresh before trying again.'
        );
      }
    });
  }

  // ---------------------------------------------------------------------------
  // What's On status pill
  // ---------------------------------------------------------------------------

  promoStatusLabel(promo: MiniMenuPromo): string {
    const labels: Record<MiniMenuPromoStatus, string> = {
      draft: 'Draft',
      review: 'Pending review',
      live: 'Published',
      promoted: 'Promoted',
      suspended: 'Suspended',
      deleted: 'Deleted'
    };
    return labels[promo.status] || 'Draft';
  }

  /** A promotion Evolution has not numbered yet cannot be submitted or suspended. */
  isPromoSaved(promo: MiniMenuPromo): boolean {
    return (promo.promotionId || 0) > 0 && !promo.isLegacy;
  }

  /**
   * The one line of context shown beside the pill. Every row uses this same shape, and the
   * pill popup stays a plain list of actions rather than carrying explanatory text.
   */
  promoNote(promo: MiniMenuPromo): string {
    if (promo.isLegacy) {
      return 'Legacy Foodie News; its existing schedule remains unchanged.';
    }
    if (!this.isPromoSaved(promo)) {
      return 'Not saved yet';
    }
    switch (promo.status) {
      case 'review': {
        const waited = this.reviewWaitingDays(promo);
        const target = this.promoRules.resubmitAfterWorkingDays;
        return waited >= target
          ? 'You can re-submit this review'
          : `Waiting ${waited} of ${target} working days`;
      }
      case 'suspended':
        return this.suspensionDaysLeft(promo)
          ? `Hidden for ${this.suspensionDaysLeft(promo)} more day(s)`
          : 'Needs a decision';
      case 'deleted':
        return this.deleteRestoreDaysLeft(promo)
          ? `Restorable for ${this.deleteRestoreDaysLeft(promo)} more day(s)`
          : 'Awaiting permanent removal';
      default:
        return '';
    }
  }

  /** Working days a pending promotion has been waiting on an approver. */
  reviewWaitingDays(promo: MiniMenuPromo | null): number {
    return workingDaysSince(promo?.submittedOn);
  }

  /** The re-submit prompt only unlocks once the approval SLA has actually lapsed. */
  canResubmit(promo: MiniMenuPromo): boolean {
    return this.isPromoSaved(promo)
      && this.reviewWaitingDays(promo) >= this.promoRules.resubmitAfterWorkingDays;
  }

  /** Days left before a suspended promotion forces a publish-or-delete decision. */
  suspensionDaysLeft(promo: MiniMenuPromo): number {
    return Math.max(0, this.promoRules.suspendDays - calendarDaysSince(promo.suspendedOn));
  }

  isSuspensionExpired(promo: MiniMenuPromo): boolean {
    return promo.status === 'suspended'
      && !!promo.suspendedOn
      && calendarDaysSince(promo.suspendedOn) >= this.promoRules.suspendDays;
  }

  /** Days left to restore a deleted promotion before Evolution purges it. */
  deleteRestoreDaysLeft(promo: MiniMenuPromo): number {
    return Math.max(0, this.promoRules.deleteRestoreDays - calendarDaysSince(promo.deletedOn));
  }

  canRestoreDeleted(promo: MiniMenuPromo): boolean {
    return promo.status === 'deleted' && this.deleteRestoreDaysLeft(promo) > 0;
  }

  // ---------------------------------------------------------------------------
  // Pill actions. Publishing always goes through the ERP approval queue.
  // ---------------------------------------------------------------------------

  /** Submits one promotion for approval — the promo equivalent of business/publish. */
  submitForReview(promo: MiniMenuPromo, onDone?: () => void): void {
    if (!this.isPromoSaved(promo)) {
      this.toastr.info('Save MiniMenu details first, then submit this promotion for review.');
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.submitFoodieNews(businessPartnerId, promo),
      'Promotion submitted for review. It goes live once HUNGERY approves it.',
      'The promotion could not be submitted for review.',
      onDone
    );
  }

  openResubmitPrompt(promo: MiniMenuPromo): void {
    this.promoInFlight = promo;
    this.openConfirm(this.promoResubmitModal);
  }

  confirmResubmit(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.resubmitFoodieNews(businessPartnerId, promo),
      'Review request re-sent.',
      'The review request could not be re-sent.'
    );
  }

  openSuspendPrompt(promo: MiniMenuPromo): void {
    this.promoInFlight = promo;
    this.openConfirm(this.promoSuspendModal);
  }

  confirmSuspend(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.suspendFoodieNews(businessPartnerId, promo),
      `Promotion suspended. It stays hidden for ${this.promoRules.suspendDays} days.`,
      'The promotion could not be suspended.'
    );
  }

  openDeletePrompt(promo: MiniMenuPromo, index: number): void {
    // An unsaved draft has nothing on the server to soft-delete; drop it locally.
    if (!this.isPromoSaved(promo)) {
      this.settings.promos.splice(index, 1);
      return;
    }
    this.promoInFlight = promo;
    this.openConfirm(this.promoDeleteModal);
  }

  confirmDelete(): void {
    const promo = this.promoInFlight;
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.deleteFoodieNews(businessPartnerId, promo),
      `Promotion deleted. You can restore it for ${this.promoRules.deleteRestoreDays} days.`,
      'The promotion could not be deleted.'
    );
  }

  /** Brings a suspended or recently deleted promotion back through approval. */
  restorePromo(promo: MiniMenuPromo | null): void {
    if (!promo) {
      return;
    }
    this.runPromoAction(
      promo,
      (businessPartnerId) => this.miniMenuService.restoreFoodieNews(businessPartnerId, promo),
      'Promotion sent back for review.',
      'The promotion could not be restored.'
    );
  }

  promoThumb(promo: MiniMenuPromo): string | null {
    return promo.image || null;
  }

  /**
   * Every pill action hits Evolution and answers with the refreshed site, so one runner
   * covers submit / re-submit / suspend / restore / delete.
   */
  private runPromoAction(
    promo: MiniMenuPromo,
    call: (businessPartnerId: number) => Observable<MiniMenuSiteAdmin>,
    successMessage: string,
    failureMessage: string,
    onDone?: () => void
  ): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId || this.promoBusyId !== null) {
      return;
    }
    this.promoBusyId = promo.promotionId;
    call(businessPartnerId).subscribe({
      next: (site) => {
        this.promoBusyId = null;
        this.promoInFlight = null;
        this.applySite(site);
        this.toastr.success(successMessage);
        onDone?.();
      },
      error: (error) => {
        this.promoBusyId = null;
        this.promoInFlight = null;
        this.toastr.error(ErrorHandlerService.bodyMessage(error) || failureMessage);
      }
    });
  }

  private openConfirm(template: TemplateRef<any>, dismissible = true): void {
    if (!template) {
      return;
    }
    this.modalService.open(template, {
      windowClass: 'project-modal warning-section-modal',
      centered: true,
      backdropClass: 'primary-backdrop',
      backdrop: dismissible ? true : 'static',
      keyboard: dismissible
    });
  }

  /** After a save, offer to submit the drafts that have never been through approval. */
  private promptSubmitDrafts(): void {
    // An unresolved suspension takes priority — never stack two prompts.
    if (this.promoInFlight || !this.submittableDrafts().length) {
      return;
    }
    this.openConfirm(this.promoSubmitModal);
  }

  submittableDrafts(): MiniMenuPromo[] {
    return this.settings.promos.filter((promo) => promo.status === 'draft' && this.isPromoSaved(promo));
  }

  /**
   * Submits every saved draft, one request at a time. Each response replaces the promo list,
   * so the next draft is picked from the refreshed list rather than a stale snapshot.
   */
  confirmSubmitDrafts(): void {
    const next = this.submittableDrafts()[0];
    if (!next) {
      return;
    }
    this.submitForReview(next, () => this.confirmSubmitDrafts());
  }

  /**
   * A suspension that has run its course must be resolved. This fires when the MiniMenu
   * form loads — the surface the vendor lands on after signing in — and the modal cannot
   * be dismissed without choosing publish or delete.
   */
  private promptExpiredSuspensions(): void {
    const expired = this.settings.promos.find((promo) => this.isSuspensionExpired(promo));
    if (!expired || this.promoInFlight) {
      return;
    }
    this.promoInFlight = expired;
    this.openConfirm(this.promoSuspensionExpiredModal, false);
  }

  onFontChange(option: KeyTitleOption): void {
    this.fontSelect = option || this.fonts[0];
    this.settings.theme.font = this.fontSelect.id;
  }

  /** CSS stack for the chosen theme font, so the section preview renders in it. */
  get themeFontStack(): string {
    return miniMenuFontStack(this.settings.theme.font);
  }

  /** Opens the appearance editor: controls beside a live miniature of the landing screen. */
  openAppearanceModal(): void {
    const modalRef = this.modalService.open(MiniMenuAppearanceModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-appearance-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.theme = JSON.parse(JSON.stringify(this.settings.theme));
    modalRef.componentInstance.businessName = this.businessDetails?.name || 'Your Business';
    modalRef.componentInstance.locationTag = this.settings.locationTag;
    modalRef.componentInstance.eyebrow = this.settings.eyebrow;
    modalRef.componentInstance.subline = this.settings.subline;

    modalRef.result.then(
      (result: MiniMenuAppearanceResult) => {
        if (!result) {
          return;
        }
        this.settings.theme.font = result.font;
        this.settings.theme.backgroundColor = result.backgroundColor;
        this.settings.theme.uiColor = result.uiColor;
        this.settings.theme.contentColor = result.contentColor;
        this.settings.theme.accentColor = result.accentColor;
        this.fontSelect = this.fonts.find((font) => font.id === result.font) || this.fonts[0];
      },
      () => { /* dismissed — theme unchanged */ }
    );
  }

  /**
   * Opens the QR poster designer: a live poster composite beside its controls. Nothing is
   * persisted — the modal's product is a downloaded SVG, so there is no result to handle.
   */
  openQrPosterModal(): void {
    const modalRef = this.modalService.open(MiniMenuQrPosterModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-qr-poster-modal',
      centered: true,
      backdrop: 'static',
      scrollable: true
    });
    modalRef.componentInstance.businessName = this.businessDetails?.name || 'Your Business';
    modalRef.componentInstance.businessDetail = [this.businessDetails?.cityName, this.businessDetails?.cuisineName]
      .filter(Boolean)
      .join(' · ');
    modalRef.componentInstance.menuUrl = this.publicMenuUrl;
    modalRef.result.then(() => { /* closed */ }, () => { /* dismissed */ });
  }

  onLanguageChange(option: KeyTitleOption): void {
    this.languageSelect = option || this.languages[0];
    this.settings.defaultLanguage = this.languageSelect.id as MiniMenuSettings['defaultLanguage'];
  }

  /**
   * Landing background: ONE image carrying three device crops, per MENU_IMAGE_FRAME_RATIOS.landing.
   * The picked file goes through the crop step first, then uploads and has its crops stored — the
   * same two-call shape the promo image uses (upload, then crop).
   */
  async onImageSelected(event: Event, target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'): Promise<void> {
    if (this.isSaving) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    if (file.size > UPLOAD_MAX_DECODE_BYTES) {
      this.toastr.error(`Please pick an image under ${formatBytes(UPLOAD_MAX_DECODE_BYTES)}.`);
      return;
    }
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!businessPartnerId) {
      return;
    }

    // Downscale before the crop step: the crops chosen there are stored in the
    // pixel space of the file that gets uploaded, and Evolution keeps the bytes
    // it is handed. isSaving doubles as the "working" flag for the picker.
    this.isSaving = true;
    const prepared = await downscaleImageFile(file);
    if (prepared.file.size > UPLOAD_MAX_BYTES) {
      this.isSaving = false;
      this.toastr.error(`Please pick an image under ${formatBytes(UPLOAD_MAX_BYTES)}.`);
      return;
    }
    const source = prepared.file;

    const reader = new FileReader();
    reader.onload = () => {
      this.isSaving = false;
      const modalRef = this.openLandingCropModal(String(reader.result || ''), emptyPromoCrops());
      modalRef.result.then(
        (result: MiniMenuLandingCropResult) => {
          if (result.removed) {
            return;
          }
          this.uploadLandingBackground(businessPartnerId, result.replacementFile || source, result.crops);
        },
        () => { /* dismissed — nothing uploaded */ }
      );
    };
    reader.onerror = () => {
      this.isSaving = false;
      this.toastr.error('The selected image could not be prepared.');
    };
    reader.readAsDataURL(source);
  }

  private openLandingCropModal(imageSource: string, crops: MiniMenuPromoCrops): NgbModalRef {
    const modalRef = this.modalService.open(MiniMenuLandingCropModalComponent, {
      windowClass: 'project-modal edit-food-menu-modal minimenu-landing-crop-modal',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.imageSource = imageSource;
    modalRef.componentInstance.crops = crops;
    return modalRef;
  }

  private uploadLandingBackground(
    businessPartnerId: number,
    file: File,
    crops: MiniMenuPromoCrops
  ): void {
    this.isSaving = true;
    this.miniMenuService.uploadMedia(businessPartnerId, 1, file).pipe(
      switchMap((asset) => this.miniMenuService.updateMediaCrop(
        businessPartnerId,
        asset.id,
        crops,
        'Landing background'
      ))
    ).subscribe({
      next: (asset) => {
        this.setPresentationMedia('teaserBackgroundUrl', asset);
        this.landingCrops = crops;
        this.isSaving = false;
      },
      error: (error) => {
        this.isSaving = false;
        this.toastr.error(error?.error?.message || 'The MiniMenu image could not be uploaded.');
      }
    });
  }

  /** Re-opens the crop step for the landing image already on file. */
  editLandingCrops(): void {
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    const asset = this.presentationMedia('teaserBackgroundUrl');
    if (!businessPartnerId || !asset || !this.settings.teaserBackgroundUrl) {
      return;
    }

    const modalRef = this.openLandingCropModal(this.settings.teaserBackgroundUrl, this.landingCrops);
    modalRef.result.then(
      (result: MiniMenuLandingCropResult) => {
        if (result.removed) {
          this.clearImage('teaserBackgroundUrl');
          return;
        }
        // A replacement is a fresh upload; otherwise only the crops changed.
        if (result.replacementFile) {
          this.uploadLandingBackground(businessPartnerId, result.replacementFile, result.crops);
          return;
        }
        this.isSaving = true;
        this.miniMenuService.updateMediaCrop(businessPartnerId, asset.id, result.crops, 'Landing background')
          .subscribe({
            next: (updated) => {
              this.setPresentationMedia('teaserBackgroundUrl', updated);
              this.landingCrops = result.crops;
              this.isSaving = false;
              this.toastr.success('Landing background crops saved.');
            },
            error: (error) => {
              this.isSaving = false;
              this.toastr.error(error?.error?.message || 'The crop could not be saved.');
            }
          });
      },
      () => { /* dismissed — crops unchanged */ }
    );
  }

  clearImage(target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'): void {
    const asset = this.presentationMedia(target);
    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (!asset || !businessPartnerId) {
      this.setPresentationMedia(target, null);
      return;
    }
    this.miniMenuService.deleteMedia(businessPartnerId, asset.id).subscribe({
      next: () => this.setPresentationMedia(target, null),
      error: () => this.toastr.error('The MiniMenu image could not be removed.')
    });
  }

  private patchFromSettings(): void {
    this.formGroup.patchValue({
      LocationTag: this.settings.locationTag,
      Eyebrow: this.settings.eyebrow,
      Subline: this.settings.subline
    });
    this.fontSelect = this.fonts.find((font) => font.id === this.settings.theme.font) || this.fonts[0];
    this.languageSelect = this.languages.find((lang) => lang.id === this.settings.defaultLanguage) || this.languages[0];
  }

  private collectIntoSettings(): void {
    this.settings.locationTag = String(this.formGroup.get('LocationTag')?.value || '').slice(0, 24);
    this.settings.eyebrow = this.formGroup.get('Eyebrow')?.value || '';
    this.settings.subline = this.formGroup.get('Subline')?.value || '';
    this.settings.theme.font = this.fontSelect?.id || 'hanken';
    this.settings.defaultLanguage = (this.languageSelect?.id as MiniMenuSettings['defaultLanguage']) || 'en';
  }

  menuTranslation(entityType: 1 | 2, entityId: number): MiniMenuMenuText {
    let record = this.translation.menu.find((item) =>
      item.entityType === entityType && item.entityId === entityId);
    if (!record) {
      record = { entityType, entityId, name: '', description: '' };
      this.translation.menu.push(record);
    }
    return record;
  }

  promoTranslation(promotionId: number): MiniMenuFoodieNewsText {
    let record = this.translation.foodieNews.find((item) => item.foodieNewsId === promotionId);
    if (!record) {
      record = {
        foodieNewsId: promotionId,
        badge: '',
        title: '',
        availabilityText: '',
        lead: '',
        body: ''
      };
      this.translation.foodieNews.push(record);
    }
    return record;
  }

  private presentationMedia(
    target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl'
  ): MiniMenuMediaAsset | null {
    if (!this.site?.presentation) {
      return null;
    }
    if (target === 'teaserBackgroundUrl') {
      return this.site.presentation.landingBackground;
    }
    return target === 'landingLogoUrl'
      ? this.site.presentation.landingLogo
      : this.site.presentation.bannerLogo;
  }

  private setPresentationMedia(
    target: 'teaserBackgroundUrl' | 'landingLogoUrl' | 'bannerLogoUrl',
    asset: MiniMenuMediaAsset | null
  ): void {
    if (this.site?.presentation) {
      if (target === 'teaserBackgroundUrl') {
        this.site.presentation.landingBackground = asset;
      } else if (target === 'landingLogoUrl') {
        this.site.presentation.landingLogo = asset;
      } else {
        this.site.presentation.bannerLogo = asset;
      }
    }
    const previewKey = target === 'teaserBackgroundUrl'
      ? 'presentation-background'
      : target === 'landingLogoUrl'
        ? 'presentation-landing-logo'
        : 'presentation-banner-logo';
    this.revokeAdminMediaPreview(previewKey);
    if (target === 'teaserBackgroundUrl') {
      this.settings.teaserBackgroundUrl = null;
    } else {
      this.settings.theme[target] = null;
    }

    const businessPartnerId = this.businessDetails?.businessPartnerId;
    if (asset && businessPartnerId) {
      this.loadAdminMediaPreview(previewKey, businessPartnerId, asset, (url) => {
        if (target === 'teaserBackgroundUrl') {
          this.settings.teaserBackgroundUrl = url;
        } else {
          this.settings.theme[target] = url;
        }
      });
    }
  }

  private emptyTranslation(): MiniMenuTranslationBundle {
    return {
      languageCode: 'zh-CN',
      site: { locationTag: '', eyebrow: '', subline: '' },
      menu: [],
      promotions: [],
      foodieNews: []
    };
  }
}
